import { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

interface ArticleSection {
  id: string;
  title: string;
  paragraphs: string[];
}

interface FAQItem {
  question: string;
  answer: string;
}

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  category: string;
  readTime: string;
  heroImageUrl: string;
  introduction: string[];
  sections: ArticleSection[];
  faqs: FAQItem[];
  conclusion: string[];
}

export default function ArticleLayout({
  title,
  subtitle,
  category,
  readTime,
  heroImageUrl,
  introduction,
  sections,
  faqs,
  conclusion,
}: ArticleLayoutProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.click';
  const pageUrl = `${siteUrl}${location.pathname}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": title,
        "description": subtitle,
        "image": heroImageUrl,
        "datePublished": "2026-08-02",
        "dateModified": "2026-08-02",
        "author": {
          "@type": "Organization",
          "name": "Vigorbuy Spread Team"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Vigorbuy Spreadsheet Resource Center",
          "url": siteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": pageUrl
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": f.answer.replace(/<[^>]*>/g, '')
          }
        }))
      }
    ]
  }), [title, subtitle, heroImageUrl, pageUrl, siteUrl, faqs]);

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <article className="pt-20 md:pt-24 pb-8">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-500 transition-colors no-underline">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600 font-medium">{title}</span>
          </nav>

          <header className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100/70 text-primary-700 text-xs font-medium mb-4">
              <i className="ri-book-open-line"></i>
              <span className="whitespace-nowrap">{category}</span>
            </div>

            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-extrabold text-foreground-900 leading-tight mb-4">
              {title}
            </h1>

            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">{subtitle}</p>

            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                <span>Updated: August 2, 2026</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                <span>{readTime}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                <span>By Vigorbuy Spread Team</span>
              </div>
            </div>
          </header>

          <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-10 bg-background-100">
            <img
              src={heroImageUrl}
              alt={title}
              title={title}
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="prose-custom">
            {/* Table of Contents */}
            <section className="mb-10 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Table of Contents</h3>
              <ol className="space-y-2 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-primary-500 mt-0.5">1.</span>
                  <a href="#introduction" className="hover:text-primary-500 transition-colors no-underline">Introduction</a>
                </li>
                {sections.map((section, idx) => (
                  <li key={section.id} className="flex items-start gap-2">
                    <span className="font-semibold text-primary-500 mt-0.5">{idx + 2}.</span>
                    <a href={`#${section.id}`} className="hover:text-primary-500 transition-colors no-underline">{section.title}</a>
                  </li>
                ))}
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-primary-500 mt-0.5">{sections.length + 2}.</span>
                  <a href="#faq" className="hover:text-primary-500 transition-colors no-underline">Frequently Asked Questions</a>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-primary-500 mt-0.5">{sections.length + 3}.</span>
                  <a href="#conclusion" className="hover:text-primary-500 transition-colors no-underline">Conclusion</a>
                </li>
              </ol>
            </section>

            {/* Introduction */}
            <section id="introduction" className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Introduction</h2>
              {introduction.map((para, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: para }}></p>
              ))}
            </section>

            {/* Body Sections */}
            {sections.map((section, idx) => (
              <section key={section.id} id={section.id} className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  {idx + 1}. {section.title}
                </h2>
                {section.paragraphs.map((para, pi) => (
                  <p key={pi} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: para }}></p>
                ))}
              </section>
            ))}

            {/* FAQ */}
            <section id="faq" className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-1">{faq.question}</h4>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }}></p>
                  </div>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section id="conclusion" className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Conclusion</h2>
              {conclusion.map((para, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: para }}></p>
              ))}
            </section>

            {/* Internal links CTA */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-background-50 mb-10">
              <div className="flex-1">
                <h3 className="text-lg font-heading font-bold mb-2">Explore More Vigorbuy Spreadsheet Resources</h3>
                <p className="text-sm text-background-100/80 leading-relaxed">
                  Browse all 14 product categories, read more guides, or visit the main website to start shopping.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-home-line"></i>
                  <span>Back to Home</span>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-shopping-cart-line"></i>
                  <span>Vigorbuy Spreadsheet</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}